/**
 * PostHog Analytics Configuration
 *
 * Centralized PostHog setup for the Easel platform.
 * Uses environment variables for API key configuration.
 */

import posthog from 'posthog-js'

// PostHog configuration
const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_KEY || ''
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

// Feature flags
const IS_DEV = import.meta.env.DEV
const POSTHOG_ENABLED = !!POSTHOG_API_KEY

/**
 * PostHog initialization options
 */
export const posthogOptions = {
  api_host: POSTHOG_HOST,
  // Only create person profiles for identified users (not anonymous)
  person_profiles: 'identified_only' as const,
  // A $pageview on load and on every client-side navigation. `true` only captures the first load,
  // which in this single-page app is one view per visit. The Arthaus harness: every property
  // sends $pageview this way (docs/admin/posthog-harness.md).
  capture_pageview: 'history_change' as const,
  // Enable autocapture for clicks, form submissions, etc.
  autocapture: true,
  // Capture performance metrics
  capture_performance: true,
  // Debug mode in development
  debug: IS_DEV,
  // Disable in development if no key provided
  disable_session_recording: IS_DEV,
  // Respect Do Not Track browser setting
  respect_dnt: true,
  // Persistence mechanism
  persistence: 'localStorage+cookie' as const,
  // Secure cookies in production
  secure_cookie: !IS_DEV,
  // Tag every event (incl. autocapture) with the product surface so all Arthaus surfaces
  // reporting into project 26575 can be split by `surface`. Registered here, before the first
  // $pageview is sent, rather than after init returns, which would leave that one untagged.
  loaded: (client: { register: (props: Record<string, unknown>) => void }) => { client.register({ surface: 'admin' }) },
}

/**
 * Initialize PostHog client
 * Should be called once at app startup
 */
export function initPostHog() {
  if (!POSTHOG_ENABLED) {
    if (IS_DEV) {
      console.log('[PostHog] Disabled - No API key provided')
    }
    return null
  }

  posthog.init(POSTHOG_API_KEY, posthogOptions)

  if (IS_DEV) {
    console.log('[PostHog] Initialized')
  }

  return posthog
}

/**
 * Get the PostHog client instance
 * Returns null if PostHog is not enabled
 */
export function getPostHog() {
  if (!POSTHOG_ENABLED) {
    return null
  }
  return posthog
}

/**
 * Check if PostHog is enabled
 */
export function isPostHogEnabled() {
  return POSTHOG_ENABLED
}

export { posthog }
export default posthog
